import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Viewer } from "@photo-sphere-viewer/core";
import { MarkersPlugin } from "@photo-sphere-viewer/markers-plugin";
import "@photo-sphere-viewer/core/index.css";
import "@photo-sphere-viewer/markers-plugin/index.css";
import { apiJson } from "../api/apiClient";
import { useSettings } from "../context/SettingsContext";
import "../styles/FloorPlanPage.css";

const toRad = (deg) => parseFloat((deg * (Math.PI / 180)).toFixed(6));

// Status configuration
const STATUS_CONFIG = {
  available:    { color: "#22c55e", label: "Available"    },
  "pre-booked": { color: "#f59e0b", label: "Pre-Booked"   },
  booked:       { color: "#ef4444", label: "Booked"       },
  sold:         { color: "#ef4444", label: "Sold"         },
};

const getStatus = (raw) => {
  const key = (raw || "").toLowerCase().replace(/\s+/g, "-");
  return STATUS_CONFIG[key] || { color: "#94a3b8", label: raw || "Unknown" };
};

const isAvailable = (raw) => (raw || "").toLowerCase() === "available";

// Marker HTML builder — plot number always visible, colored by status,
// with a small pulsing dot for a bit of life rather than a static badge.
const markerHtml = (label, status) => {
  const cfg = getStatus(status);
  return `
    <div class="unit-marker" style="--status-color:${cfg.color}">
      <span class="unit-marker__pulse"></span>
      <span class="unit-marker__label">${label}</span>
    </div>`;
};

const FloorPlanPage = () => {
  const { tenant, floorId } = useParams();
  const navigate = useNavigate();

  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const overlayRef = useRef(null);
  const labelRef = useRef(null);
  const intervalRef = useRef(null);
  const markersPluginRef = useRef(null);

  const { settings, isLoaded: settingsLoaded } = useSettings();

  const [loadProgress, setLoadProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showHeader, setShowHeader] = useState(false);
  const [panoramaUrl, setPanoramaUrl] = useState(null);
  const [hotspots, setHotspots] = useState(null);
  const [blockName, setBlockName] = useState("");
  const [masterPlanId, setMasterPlanId] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [popup, setPopup] = useState(null);

  // ── Helper: build image URL with tenant ──────────────────────────────────
  const getFullImageUrl = useCallback((path) => {
    if (!path) return null;
    // If it's already a full URL, return it as-is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    // Otherwise, build tenant-scoped URL
    const baseUrl = settings?.img_url || process.env.REACT_APP_IMG_URL || '';
    return `${baseUrl}/storage/${path}`;
  }, [settings]);

  // ── Fetch block + hotspots ──────────────────────────────────────────────
  useEffect(() => {
    if (!tenant || !floorId) {
      setFetchError("No tenant or block ID provided.");
      setIsFetching(false);
      return;
    }

    const fetchAll = async () => {
      try {
        // Fetch block details using tenant-aware apiJson
        const blockData = await apiJson(tenant, `/block/${floorId}`);

        if (!blockData.panorama_url) {
          throw new Error("No panorama uploaded for this floor yet.");
        }

        // Get the full image URL with tenant context
        const fullPanoramaUrl = getFullImageUrl(blockData.panorama_url);
        setPanoramaUrl(fullPanoramaUrl);
        setBlockName(blockData.name || `Floor ${floorId}`);
        setMasterPlanId(blockData.master_plan_id);

        // Fetch hotspots
        const rawHotspots = await apiJson(tenant, `/hotspots?block_id=${floorId}`);

        // Enrich with status
        const needsStatus = rawHotspots.some(h => !h.status);
        let enriched = rawHotspots;

        if (needsStatus) {
          enriched = await Promise.all(
            rawHotspots.map(async (h) => {
              if (h.status) return h;
              try {
                const uData = await apiJson(tenant, `/unit/${h.plot_id}`);
                return { ...h, status: uData.status || "unknown" };
              } catch {
                return { ...h, status: "unknown" };
              }
            })
          );
        }

        setHotspots(enriched);
      } catch (err) {
        console.error("Fetch error:", err);
        setFetchError(err.message);
      } finally {
        setIsFetching(false);
      }
    };

    fetchAll();
  }, [tenant, floorId, getFullImageUrl]);

  // ── Initialize PSV viewer ───────────────────────────────────────────────
  useEffect(() => {
    if (!panoramaUrl || !hotspots || !containerRef.current || viewerRef.current) return;

    let intervalId;
    let animationId;
    let cleanupFunctions = [];

    const initViewer = async () => {
      let progress = 0;
      intervalId = setInterval(() => {
        progress += Math.random() * 8;
        if (progress >= 90) {
          progress = 90;
          clearInterval(intervalId);
        }
        setLoadProgress(Math.min(Math.round(progress), 90));
      }, 200);

      const psvMarkers = hotspots.map((h) => ({
        id: `unit-${h.id}`,
        position: {
          yaw: toRad(parseFloat(h.yaw)),
          pitch: toRad(parseFloat(h.pitch)),
        },
        html: markerHtml(h.label, h.status),
        size: { width: 140, height: 40 },
        data: {
          hotspotId: h.id,
          plotId: h.plot_id,
          name: h.label,
          status: h.status,
          yawRad: toRad(parseFloat(h.yaw)),
          pitchRad: toRad(parseFloat(h.pitch)),
        },
      }));

      viewerRef.current = new Viewer({
        container: containerRef.current,
        panorama: panoramaUrl,
        defaultZoomLvl: 50,
        mousewheelCtrlKey: false,
        loadingImg: null,
        loadingTxt: "",
        navbar: ["zoom", "move", "caption", "fullscreen"],
        plugins: [[MarkersPlugin, { markers: psvMarkers }]],
      });

      viewerRef.current.addEventListener("ready", () => {
        clearInterval(intervalId);
        setLoadProgress(100);

        setTimeout(() => {
          setIsLoaded(true);
          setShowHeader(true);
        }, 700);

        // Auto-rotate effect
        let angle = 0;
        let isInteracting = false;
        let resumeTimeout = null;

        const rotate = () => {
          if (!isInteracting && viewerRef.current) {
            angle += 0.003;
            try {
              viewerRef.current.rotate({ yaw: Math.sin(angle) * 0.08, pitch: 0 });
            } catch (e) {}
          }
          if (viewerRef.current) {
            animationId = requestAnimationFrame(rotate);
          }
        };

        animationId = requestAnimationFrame(rotate);

        const onInteractionStart = () => {
          isInteracting = true;
          clearTimeout(resumeTimeout);
        };

        const onInteractionEnd = () => {
          clearTimeout(resumeTimeout);
          resumeTimeout = setTimeout(() => {
            isInteracting = false;
          }, 2000);
        };

        const container = containerRef.current;
        if (container) {
          container.addEventListener("mousedown", onInteractionStart);
          container.addEventListener("mouseup", onInteractionEnd);
          container.addEventListener("touchstart", onInteractionStart);
          container.addEventListener("touchend", onInteractionEnd);
        }

        cleanupFunctions.push(() => {
          if (animationId) cancelAnimationFrame(animationId);
          clearTimeout(resumeTimeout);
          if (container) {
            container.removeEventListener("mousedown", onInteractionStart);
            container.removeEventListener("mouseup", onInteractionEnd);
            container.removeEventListener("touchstart", onInteractionStart);
            container.removeEventListener("touchend", onInteractionEnd);
          }
        });
      });

      markersPluginRef.current = viewerRef.current.getPlugin(MarkersPlugin);

      markersPluginRef.current.addEventListener("select-marker", ({ marker }) => {
        const { plotId, name, status, yawRad, pitchRad } = marker.data;

        setPopup(null);

        if (isAvailable(status)) {
          // Available: animate and navigate
          hotspots.forEach((h) => {
            try {
              markersPluginRef.current?.updateMarker({ id: `unit-${h.id}`, opacity: 0 });
            } catch (e) {}
          });

          if (labelRef.current) {
            labelRef.current.textContent = name;
            labelRef.current.classList.add("floor-unit-label-visible");
          }

          viewerRef.current
            ?.animate({ yaw: yawRad, pitch: pitchRad, zoom: 55, speed: "4rpm" })
            .then(() => viewerRef.current?.animate({ yaw: yawRad, pitch: pitchRad, zoom: 100, speed: "20rpm" }))
            .then(() => {
              if (overlayRef.current) {
                overlayRef.current.classList.add("floor-transition-active");
              }
              setTimeout(() => navigate(`/${tenant}/unit/${plotId}`), 450);
            });
        } else {
          // Not available: show popup
          setPopup({ label: name, status, plotId });
        }
      });

      cleanupFunctions.push(() => {
        if (viewerRef.current) {
          viewerRef.current.destroy();
          viewerRef.current = null;
        }
        markersPluginRef.current = null;
      });
    };

    initViewer();

    return () => {
      clearInterval(intervalId);
      if (animationId) cancelAnimationFrame(animationId);
      cleanupFunctions.forEach(fn => fn());
    };
  }, [panoramaUrl, hotspots, navigate, tenant]);

  // ── Entry fade-in effect ─────────────────────────────────────────────────
  useEffect(() => {
    if (!overlayRef.current) return;
    overlayRef.current.style.opacity = "1";
    overlayRef.current.style.transition = "none";

    const timer = setTimeout(() => {
      if (overlayRef.current) {
        overlayRef.current.style.transition = "opacity 0.7s ease-out";
        overlayRef.current.style.opacity = "0";
      }
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  const handleBackClick = () => {
    if (overlayRef.current) {
      overlayRef.current.style.transition = "opacity 0.4s ease-in";
      overlayRef.current.style.opacity = "1";
    }
    setTimeout(() => navigate(`/${tenant}/apartment-view/${masterPlanId || ""}`), 450);
  };

  const handleViewUnitDetails = (plotId) => {
    setPopup(null);
    navigate(`/${tenant}/unit/${plotId}`);
  };

  // ── Fetching state — classy mark + sweeping rule, no emoji ──────────────
  if (isFetching || !settingsLoaded) {
    return (
      <div className="floor-fetching">
        <div className="floor-fetching-card">
          {settings?.logo_dark_url ? (
            <img
              src={settings.logo_dark_url}
              alt={settings?.company_name || "Logo"}
              className="floor-fetching-logo"
            />
          ) : (
            <div className="floor-fetching-mark">
              {(settings?.company_name || "")[0] || ""}
            </div>
          )}
          <div className="floor-fetching-rule">
            <div className="floor-fetching-rule__fill" />
          </div>
          <div className="floor-fetching-label">
            {!settingsLoaded ? "Loading" : "Preparing your floor"}
          </div>
        </div>
      </div>
    );
  }

  // ── Error state ─────────────────────────────────────────────────────────
  if (fetchError || !panoramaUrl) {
    return (
      <div className="floor-error">
        <div className="floor-error-card">
          <h2 className="floor-error-title">Floor unavailable</h2>
          <p className="floor-error-message">
            {fetchError || "Panorama not uploaded yet for this floor."}
          </p>
          <button onClick={() => navigate(`/${tenant}`)} className="floor-error-btn">
            Go back
          </button>
        </div>
      </div>
    );
  }

  // ── Main render — full-bleed panorama, no header bar ────────────────────
  return (
    <div className="floor-plan-page">
      {/* Back button — sole persistent chrome, floats top-right */}
      <button
        onClick={handleBackClick}
        className={`floor-back-btn ${showHeader ? "is-visible" : ""}`}
        aria-label="Back to building"
      >
        ←
      </button>

      {/* Panorama Container — fills the whole viewport */}
      <div ref={containerRef} className="floor-panorama" />

      {/* Loading Overlay — classy mark + hairline rule */}
      <div className={`floor-loader ${isLoaded ? 'floor-loader-hidden' : ''}`}>
        <div className="floor-loader-card">
          {settings?.logo_dark_url ? (
            <img
              src={settings.logo_dark_url}
              alt={settings?.company_name || "Logo"}
              className="floor-loader-logo"
            />
          ) : (
            <div className="floor-loader-mark">
              {(settings?.company_name || "")[0] || ""}
            </div>
          )}
          <div className="floor-loader-rule">
            <div
              className="floor-loader-rule__fill"
              style={{ width: `${loadProgress}%` }}
            />
          </div>
          <div className="floor-loader-label">
            {loadProgress < 100 ? "Preparing your floor" : "Ready"}
          </div>
        </div>
      </div>

      {/* Legend — floats top-right, below the back button */}
      {isLoaded && (
        <div className="floor-legend">
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <div key={key} className="legend-item">
              <div className="legend-dot" style={{ background: cfg.color }} />
              {cfg.label}
            </div>
          ))}
        </div>
      )}

      {/* Unit Label (on zoom) */}
      <div ref={labelRef} className="floor-unit-label" />

      {/* Status Popup (non-available units) */}
      {popup && (
        <div className="status-popup-overlay" onClick={() => setPopup(null)}>
          <div className="status-popup" onClick={e => e.stopPropagation()}>
            <button onClick={() => setPopup(null)} className="status-popup-close">
              ×
            </button>

            <div className="status-popup-label">Unit</div>
            <div className="status-popup-unit">{popup.label}</div>

            {(() => {
              const cfg = getStatus(popup.status);
              const message = cfg.label === "Pre-Booked"
                ? "This unit has a pending reservation."
                : cfg.label === "Booked"
                  ? "This unit is fully booked."
                  : cfg.label === "Sold"
                    ? "This unit has been sold."
                    : "This unit is currently unavailable.";

              return (
                <div className="status-popup-badge" style={{ background: `${cfg.color}18`, border: `1px solid ${cfg.color}44` }}>
                  <div className="status-popup-dot" style={{ background: cfg.color, boxShadow: `0 0 8px ${cfg.color}` }} />
                  <div>
                    <div className="status-popup-status" style={{ color: cfg.color }}>{cfg.label}</div>
                    <div className="status-popup-message">{message}</div>
                  </div>
                </div>
              );
            })()}

            <div className="status-popup-actions">
              <button
                onClick={() => handleViewUnitDetails(popup.plotId)}
                className="status-popup-btn-primary"
              >
                View Unit Details →
              </button>
              <button
                onClick={() => setPopup(null)}
                className="status-popup-btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transition Overlay */}
      <div ref={overlayRef} className="floor-transition" />
    </div>
  );
};

export default FloorPlanPage;
